import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

interface SentryConfig {
    app: App
    router: Router
    dsn?: string
    environment?: string
    release?: string
    isDevelopment?: boolean
}

let feedbackInstance: ReturnType<typeof Sentry.getFeedback> | null = null

export function initSentry({
    app,
    router,
    dsn,
    environment = 'development',
    release,
    isDevelopment = false,
}: SentryConfig) {
    if (!dsn) {
        console.warn('Sentry DSN not provided, skipping Sentry initialization')
        return
    }

    // Skip Sentry entirely in development
    if (isDevelopment) {
        console.log('🚫 Sentry disabled in development environment')
        return
    }

    Sentry.init({
        app,
        dsn,
        environment,
        release,

        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration({
                maskAllText: true,
                maskAllInputs: true,
                blockAllMedia: true,
                networkDetailAllowUrls: [
                    /^https:\/\/staff\.amphora\.au\/?/,
                    //   /^https:\/\/staff\.dev\.metadirector\.com\.au:8082\/?/,
                ],
                networkCaptureBodies: true,
                networkRequestHeaders: ['User-Agent', 'Accept', 'Content-Type', 'Authorization'],
                networkResponseHeaders: ['Content-Type', 'Content-Length', 'Date'],
            }),
            // User Feedback integration
            Sentry.feedbackIntegration({
                colorScheme: 'light',
                showBranding: false,
                isEmailRequired: true,
                autoInject: false,
            }),
            // HTTP Context integration
            Sentry.httpContextIntegration(),
        ],

        // Performance monitoring
        tracesSampleRate: 0.1,

        // Session Replay sampling rates
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.1,

        // Enhanced error filtering and logging
        beforeSend(event, hint) {
            const errorMessage = event.exception?.values?.[0]?.value || ''
            if (
                errorMessage.includes('Script error') ||
                errorMessage.includes('ResizeObserver loop limit exceeded')
            ) {
                return null
            }

            if (event.exception) {
                console.group('🚨 Sentry Error Captured')
                console.error('Original error:', hint.originalException)
                console.log('Sentry event:', event)
                console.groupEnd()
            }

            return event
        },

        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
                return null
            }
            return breadcrumb
        },

        // Enhanced context
        initialScope: {
            tags: {
                component: 'vue-app',
                framework: 'vue3',
                build_tool: 'vite',
            },
            contexts: {
                app: {
                    app_name: 'Amphora Staff',
                    app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
                },
            },
        },

        // Development settings
        debug: false,
        attachProps: true,
    })

    feedbackInstance = Sentry.getFeedback()

    console.log(`🔍 Sentry initialized for ${environment}`)

    window.addEventListener('unhandledrejection', (event) => {
        Sentry.captureException(event.reason)
    })
}

export const sentryUtils = {
    // Capture errors with optional context
    captureError: (error: Error, context?: Record<string, any>) => {
        Sentry.withScope((scope) => {
            if (context) {
                scope.setContext('custom', context)
            }
            Sentry.captureException(error)
        })
    },

    // Set user information
    setUser: (user: { id?: string; email?: string; username?: string } | null) => {
        Sentry.setUser(user)
    },

    // Add custom tags
    setTag: (key: string, value: string) => {
        Sentry.setTag(key, value)
    },

    async openFeedbackForm() {
        if (feedbackInstance) {
            const form = await feedbackInstance.createForm()
            form.appendToDom()
            form.open() // This method is valid on the created form
        }
    },
}

export { Sentry }
